import { AfterViewInit, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { EventsKey } from 'ol/events.js';
import { unByKey } from 'ol/Observable.js';
import { AtlasClickResultComponent } from '../atlas-click-result.component';
import { HttpClient } from '@angular/common/http';
import { BootstrapMediaService } from 'src/app/services/bootstrap-media.service';
import { TranslationHelperService } from 'src/app/services/translations/translation-helper.service';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom.js';
import { POINTS_LABEL_ATLAS_FIX } from 'src/assets/constants/constants';
import * as GeoJSONModule from 'ol/format/GeoJSON.js';
const GeoJSON = GeoJSONModule.default as typeof GeoJSONModule.default;
import { OLMapService } from 'src/app/services/ol-map/ol-map.service';
import { unsubscribe } from 'src/app/utils/helpers';
import { AtlasFixFilterService } from 'src/app/services/filter/atlas-fix-filter.service';
import { PointDetails } from 'src/app/models/pointDetails';
import { catchError, defer, finalize, map, of, switchMap, tap, timeout } from 'rxjs';
import { GeoserverAPIService } from 'src/app/services/geoserver/geoserver-api.service';
import { environment } from 'src/environments/environment';

@Component({
  standalone: false,
    selector: 'app-atlas-fix-map',
    templateUrl: './atlas-fix-map.component.html',
    styleUrls: ['./atlas-fix-map.component.scss'] })
export class AtlasFixMapComponent extends AtlasClickResultComponent implements OnInit, AfterViewInit, OnDestroy {
    isPoint: boolean = false;
    private latestClickRequestId = 0;
    private zoomKey?: EventsKey;

    public readonly LABEL_ATLASFIX_POINTS = POINTS_LABEL_ATLAS_FIX;
    get pointDetail(): PointDetails {
        return this.atlasFixFilterService.atlasFixService.pointDetails;
    }
    constructor(
        public atlasFixFilterService: AtlasFixFilterService,
        protected http: HttpClient,
        protected breakpointObserver: BootstrapMediaService,
        protected translationHelper: TranslationHelperService,
        public olMapService: OLMapService,
        private geoserverService: GeoserverAPIService,
        private readonly zone: NgZone,

    ) {
        super(http, breakpointObserver, translationHelper, olMapService);
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.atlasFixFilterService.atlasFixService.polygonSubject.subscribe((val) => {
                if (!val) {
                    return;
                }
                this.atlasFixFilterService.setQueryParams();
                this.atlasFixFilterService.atlasFixService.setDefaultFilter();
                this.loadLayer();
                this.loadPoints();
            })
        );




        this.mapOperations();
        this.zoomKey = this.olMapService.map.getView().on('change:resolution', () => {
            this.atlasFixFilterService.atlasFixService.updateLandlineLayerVisibility();
        });
        this.atlasFixFilterService.atlasFixService.updateLandlineLayerVisibility();
    }
    ngAfterViewInit(): void {
        setTimeout(() => {
            this.atlasFixFilterService.atlasFixService.getLegends('fix-legend');
            this.atlasFixFilterService.atlasFixService.getFilter('fix-filter');
            this.atlasFixFilterService.atlasFixService.getDetails('fix-detail');
        });
    }

    mapOperations(): void {
        this.subscriptions.push(this.olMapService.mapClick$.pipe(
            map((event) => ({ event, requestId: ++this.latestClickRequestId })),
            tap(() => this.zone.run(() => {
                // Clear the old selection immediately, but keep its panel data visible
                // until the active request can replace it with a complete result.
                this.atlasFixFilterService.atlasFixService.removeVectorLayers();
                this.atlasFixFilterService.atlasFixService.pointDetails = [];
                this.olMapService.detailsLoading = true;
                this.olMapService.notifyDetailStateChanged();
            })),
            switchMap(({ event, requestId }) => defer(() => {
                const endpoint = this.atlasFixFilterService.atlasFixService.createJoinEndpoints(event);
                if (!endpoint) {
                    return of({ requestId, layer: null, fc: null });
                }

                return endpoint.featureCollection.pipe(
                    map((fc) => ({ requestId, layer: endpoint.layer, fc }))
                );
            }).pipe(
                timeout(15000),
                catchError(() => of({ requestId, layer: null, fc: null })),
                finalize(() => this.zone.run(() => {
                    // switchMap cancels the previous request on a new click.
                    // Only the active request may update the shared popup state.
                    if (requestId === this.latestClickRequestId) {
                        this.olMapService.detailsLoading = false;
                        this.olMapService.notifyDetailStateChanged();
                    }
                }))
            ))
        ).subscribe((result) => {
            if (result.requestId !== this.latestClickRequestId) {
                return;
            }

            this.zone.run(() => {
                this.renderSingleResponse(
                    { layer: result.layer, fc: result.fc },
                    () => this.atlasFixFilterService.atlasFixService.removeVectorLayers(),
                    (features, layer) => {
                        // Keep the previous result visible while the next point loads,
                        // then replace the panel contents in one synchronous update.
                        this.olMapService.landlinePointDetails = [];
                        this.olMapService.details = [];

                        if (layer == environment.geoServer.statsec_details) {
                            if (features.length > 0) {
                                this.addVectorLayer(features[0], new GeoJSON(), false, 'active');
                            }
                            this.updatePointDetails(features);
                            return;
                        }

                        this.updateStatSecDetails(features);
                        const selection = this.getSelectionFeatures(features);
                        this.renderSelection(selection.activeFeature, selection.parentFeature);
                        this.showSelectionDetails(selection.activeFeature, selection.parentFeature);
                    },
                    () => this.olMapService.closeDetailPanel()
                );
                this.olMapService.notifyDetailStateChanged();
            });
        }));
    }
    private getSelectionFeatures(features: Feature[]): { activeFeature: Feature<Geometry>; parentFeature?: Feature<Geometry> } {
        const reduced = this.reduceToOnePerLevel(features);
        const selectedLevel = this.atlasFixFilterService.atlasFixService.getLayerName();
        const activeFeature = reduced[selectedLevel] ?? features[0];
        const parentFeature = selectedLevel === 'statsec' ? reduced.municipality : undefined;

        return { activeFeature, parentFeature };
    }

    private getParentFeatureForLevel(
        reduced: Record<string, Feature<Geometry> | undefined>,
        selectedLevel: string
    ): Feature<Geometry> | undefined {
        const parentCandidates: Record<string, string[]> = {
            statsec: ['municipality', 'province'],
            municipality: ['province', 'region', 'country'],
            district: ['province', 'region', 'country'],
            province: ['region', 'country'],
            region: ['country'],
            country: [],
        };

        return (parentCandidates[selectedLevel] ?? [])
            .map((level) => reduced[level])
            .find((feature) => !!feature);
    }

    private renderSelection(activeFeature: Feature<Geometry>, parentFeature?: Feature<Geometry>): void {
        if (parentFeature) {
            this.addVectorLayer(parentFeature, new GeoJSON(), true, 'parent');
        }
        this.addVectorLayer(activeFeature, new GeoJSON(), false, 'active');
    }

    private showSelectionDetails(activeFeature: Feature<Geometry>, parentFeature?: Feature<Geometry>): void {
        const parentName = parentFeature?.get('name') ?? parentFeature?.get('alternative_name');
        if (parentName && parentFeature?.get('level') !== 'country') {
            this.olMapService.details.push({
                label: 'MUNICIPALITY',
                value: parentName,
            });
        }

        this.olMapService.details.push({
            label: 'name',
            value: activeFeature.get('name'),
        });
        this.olMapService.details.push({
            label: 'cover',
            value: `${((activeFeature.get('filtervalue') as number) * 100).toFixed(2)}%`,
        });
    }

    getLayerDetails(feature: Feature<Geometry>): void {
        this.atlasFixFilterService.atlasFixService.getLayerDetails(feature);
    }

    getLayerName(): string {
        return this.atlasFixFilterService.atlasFixService.getLayerName();
    }
    getStyleName(): string {
        return this.atlasFixFilterService.atlasFixService.getStyleName();
    }
    loadLayer(): void {
        this.atlasFixFilterService.atlasFixService.loadLayer();
    }
    loadPoints(): void {
        this.atlasFixFilterService.atlasFixService.loadPoint(null);
    }
    setFilter(): void {
        this.atlasFixFilterService.atlasFixService.setFilter();
    }
    updateDetails(feature: Feature<Geometry>): void {
        this.atlasFixFilterService.atlasFixService.updateDetails(feature);
    }

    updateStatSecDetails(features: Feature<Geometry>[]): void {
        this.atlasFixFilterService.atlasFixService.updateStatSecDetails(features);
    }

    updatePointDetails(features: Feature<Geometry>[]): void {
        this.atlasFixFilterService.atlasFixService.updatePointDetails(features);
    }

    addVectorLayer(feature: Feature<Geometry>, format: any, blackBoundries?: boolean, role: 'active' | 'parent' = 'active') {
        this.atlasFixFilterService.atlasFixService.addVectorLayer(feature, format, blackBoundries, role);
    }
    ngOnDestroy(): void {
        if (this.zoomKey) unByKey(this.zoomKey);
        this.atlasFixFilterService.atlasFixService.mapOperationsUn();
        unsubscribe(this.subscriptions);
    }
    reduceToOnePerLevel(features: Feature[]): Record<string, Feature<Geometry> | undefined> {
        const levels = ['statsec', 'municipality', 'province', 'district', 'region', 'country'];
        const byLevel: Record<string, Feature[]> = Object.fromEntries(levels.map((level) => [level, []]));

        features.forEach((f) => {
            const level = f.get('level');
            if (level && byLevel[level]) {
                byLevel[level].push(f);
            }
        });

        const reduce = (level: string): Feature<Geometry> | undefined => {
            const group = byLevel[level];
            if (!group.length) return undefined;

            const seen = new Set<string>();
            const unique = group.filter((f) => {
                const geomStr = new GeoJSON().writeGeometry(f.getGeometry());
                if (seen.has(geomStr)) return false;
                seen.add(geomStr);
                return true;
            });

            return unique[0] as Feature<Geometry>;
        };

        return Object.fromEntries(levels.map((level) => [level, reduce(level)]));
    }
}